import { motion } from "framer-motion";
import { Battery, Zap, TrendingUp, Building2, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/animations/ScrollAnimations";
import { Link } from "react-router-dom";
import zumV2GDepot from "@/assets/zum-v2g-depot.png";

const caseStudyStats = [
  {
    value: "900",
    unit: "MW",
    label: "Energy Discharged to Grid",
    icon: Zap,
  },
  {
    value: "74",
    unit: "",
    label: "V2G Chargers Deployed",
    icon: Battery,
  },
  {
    value: "100%",
    unit: "",
    label: "Fleet Electrification",
    icon: TrendingUp,
  },
  {
    value: "#1",
    unit: "",
    label: "Largest V2G Depot in US",
    icon: Building2,
  },
];

const keyFeatures = [
  "Bidirectional charging with school bus fleet",
  "Grid services participation generating revenue",
  "Demand response and energy arbitrage",
  "Full fleet management integration",
  "Real-time monitoring and optimization",
  "Utility partnership and grid stabilization",
];

export function V2GCaseStudy() {
  return (
    <section className="py-32 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-card/20 to-background" />
      <div className="absolute inset-0">
        <motion.div 
          className="absolute top-20 left-10 w-[500px] h-[500px] bg-accent/8 rounded-full blur-[120px]"
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-20 right-10 w-[600px] h-[600px] bg-primary/8 rounded-full blur-[120px]"
          animate={{ 
            scale: [1.1, 1, 1.1],
            opacity: [0.4, 0.2, 0.4]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Section Dividers */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border/50 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border/50 to-transparent" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <ScrollReveal className="text-center mb-20">
          <span className="inline-flex items-center gap-2 glass-card px-5 py-2.5 rounded-full text-sm font-medium text-accent mb-8">
            <Battery className="h-4 w-4" />
            Featured Case Study
          </span>
          <h2 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
            Powering the <span className="text-gradient">Largest V2G Depot</span>
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            Discover how EvGateway's V2G technology is transforming electric school bus fleets into grid assets, 
            delivering unprecedented value back to the grid.
          </p>
        </ScrollReveal>

        {/* Stats Grid */}
        <StaggerContainer className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-20" staggerDelay={0.1}>
          {caseStudyStats.map((stat, idx) => (
            <StaggerItem key={idx}>
              <motion.div
                whileHover={{ scale: 1.03, y: -6 }}
                className="glass-card rounded-3xl p-8 text-center group cursor-default"
              >
                <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                  <stat.icon className="h-7 w-7 text-accent" />
                </div>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl md:text-5xl font-bold text-accent">{stat.value}</span>
                  {stat.unit && <span className="text-xl font-semibold text-accent">{stat.unit}</span>}
                </div>
                <p className="text-sm text-muted-foreground mt-3 font-medium">{stat.label}</p>
              </motion.div>
            </StaggerItem>
          ))}
        </StaggerContainer>

        {/* Case Study Content */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Content */}
          <ScrollReveal>
            <div className="space-y-8">
              <div>
                <span className="text-accent font-semibold uppercase tracking-widest text-sm">ZUM Services Partnership</span>
                <h3 className="text-3xl md:text-4xl font-bold mt-3 mb-5">
                  Revolutionizing School Bus Fleet Electrification
                </h3>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Zum Services, a leading school bus transportation provider, partnered with EvGateway to deploy 
                  one of the nation's largest Vehicle-to-Grid (V2G) enabled electric school bus depots. 
                  This groundbreaking project demonstrates how electric fleets can serve as mobile energy 
                  storage assets while maintaining operational excellence.
                </p>
              </div>

              <div className="space-y-4">
                {keyFeatures.map((feature, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.08 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-4 group"
                  >
                    <div className="w-8 h-8 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0 group-hover:bg-accent/20 transition-colors">
                      <CheckCircle2 className="h-4 w-4 text-accent" />
                    </div>
                    <span className="text-foreground/90 font-medium">{feature}</span>
                  </motion.div>
                ))}
              </div>

              <div className="flex flex-wrap gap-4 pt-4">
                <Button variant="hero" asChild className="group">
                  <Link to="/contact">
                    Start Your V2G Project
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
                <Button variant="outline" className="border-foreground/30 text-foreground hover:bg-foreground/10 hover:border-foreground/50 rounded-xl" asChild>
                  <a href="/downloads/zum-v2g-case-study.pdf" download>
                    Download Case Study
                  </a>
                </Button>
              </div>
            </div>
          </ScrollReveal>

          {/* Right - Visual */}
          <ScrollReveal delay={0.2}>
            <motion.div 
              className="relative"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              {/* V2G Depot Image */}
              <div className="relative rounded-3xl overflow-hidden border border-border/30 shadow-2xl glass-card p-2">
                <img
                  src={zumV2GDepot}
                  alt="ZUM electric school bus V2G depot powered by EvGateway"
                  className="w-full h-auto rounded-2xl"
                />
                
                {/* Overlay Stats */}
                <div className="absolute bottom-2 left-2 right-2 p-6 bg-gradient-to-t from-background/98 via-background/90 to-transparent rounded-b-2xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-3xl font-bold text-accent">900 MW</div>
                      <div className="text-sm text-muted-foreground font-medium">Successfully Discharged</div>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-primary">74 Chargers</div>
                      <div className="text-sm text-muted-foreground font-medium">V2G Enabled</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-accent/15 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute -top-8 -left-8 w-32 h-32 bg-primary/15 rounded-full blur-3xl pointer-events-none" />
            </motion.div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
